import React from "react";
import {
  Check,
  ChevronDown,
  ChevronRight,
  Cloud,
  Copy,
  History,
  Pencil,
  Save,
  Share2,
  Trash2,
  X,
} from "lucide-react";
import {
  createVersionEmbedLink,
  createVersionShareLink,
  deleteProject,
  deleteVersion,
  labelVersion,
  listVersionEmbedLinks,
  listVersionShareLinks,
  listProjects,
  listVersions,
  patchProject,
  revokeVersionShareLink,
} from "../../cloud/api";
import { copyTextToClipboard } from "../../physics/physicsFailureReport";
import MenuSelect from "../ui/MenuSelect";
import Tooltip from "../ui/Tooltip";
import DraggablePanel from "./DraggablePanel";

const PAGE_SIZE = 25;
const ROW_ACTION_BUTTON_CLASS =
  "flex h-7 w-7 shrink-0 items-center justify-center rounded text-gray-400 transition-colors hover:text-gray-100 focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-500 disabled:cursor-not-allowed disabled:opacity-50";
const ROW_DELETE_CONFIRM_CLASS =
  "flex h-7 shrink-0 items-center justify-center rounded border border-red-500 bg-red-600 px-2 text-xs font-medium text-white transition-colors hover:bg-red-500 focus:outline-none focus-visible:ring-1 focus-visible:ring-red-300 disabled:cursor-not-allowed disabled:opacity-60";
const SHARE_LINK_ROLES = {
  viewer: {
    label: "Read-only",
    description: "Shared viewers can open this saved version.",
    copiedToast: "Read-only share link copied.",
  },
  editor: {
    label: "Editable",
    description: "Shared editors can open this version and save new versions.",
    copiedToast: "Editable share link copied.",
  },
};
const SHARE_LINK_ROLE_OPTIONS = [
  {
    value: "viewer",
    label: "Read-only",
    description: "Viewers can open this saved version.",
  },
  {
    value: "editor",
    label: "Editable",
    description: "Editors can open it and save new versions.",
  },
];
const EMBED_IFRAME_WIDTH = 640;
const EMBED_IFRAME_HEIGHT = 480;

function shareLinkRole(link) {
  return link?.role === "editor" ? "editor" : "viewer";
}

function activeShareLinkForRole(links, role) {
  return (
    links.find(
      (link) => !link.revokedAt && link.kind !== "embed" && shareLinkRole(link) === role
    ) || null
  );
}

function activeEmbedLink(links) {
  return links.find((link) => !link.revokedAt && link.kind === "embed") || null;
}

function uniqueShareLinksById(links) {
  return Array.from(new Map(links.map((link) => [link.id, link])).values());
}

function embedPathForLink(link) {
  if (link?.kind !== "embed" || !link.id) return "";
  return `/embed-viewer?embed=${encodeURIComponent(link.id)}`;
}

function embedOriginForCurrentPage() {
  if (typeof window === "undefined") return "";
  const configuredEmbedBaseUrl = import.meta.env.VITE_EMBED_BASE_URL;
  if (typeof configuredEmbedBaseUrl === "string" && configuredEmbedBaseUrl.trim()) {
    return configuredEmbedBaseUrl.trim();
  }
  return window.location.origin;
}

function embedIframeCode(url) {
  if (!url) return "";
  return `<${"iframe"} src="${url}" width="${EMBED_IFRAME_WIDTH}" height="${EMBED_IFRAME_HEIGHT}" loading="lazy" title="Critter Engine embed" style="border:0"></iframe>`;
}

function formatDate(epochMs) {
  if (!epochMs) return "Unknown";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(epochMs));
}

function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value >= 10 || unitIndex === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[unitIndex]}`;
}

function versionTitle(version) {
  if (version.label) return version.label;
  if (version.kind === "autosave") return `Autosave #${version.versionNumber}`;
  return `Manual save #${version.versionNumber}`;
}

function versionCountsFromVersions(versions) {
  const autosaveVersionCount = Array.isArray(versions)
    ? versions.filter((version) => version.kind === "autosave").length
    : 0;
  const manualVersionCount = Array.isArray(versions) ? versions.length - autosaveVersionCount : 0;
  return {
    autosaveVersionCount,
    manualVersionCount,
    autosaveOnly: autosaveVersionCount > 0 && manualVersionCount === 0,
  };
}

function dispatchCloudToast(message, tone = "info") {
  window.dispatchEvent(
    new CustomEvent("critter-toast", {
      detail: {
        message,
        tone,
      },
    })
  );
}

function isAutosaveOnlyProject(project) {
  if (
    Number.isFinite(project?.autosaveVersionCount) ||
    Number.isFinite(project?.manualVersionCount)
  ) {
    return (project.autosaveVersionCount ?? 0) > 0 && (project.manualVersionCount ?? 0) === 0;
  }
  return Boolean(project?.autosaveOnly);
}

const CloudProjectsPanel = React.memo(function CloudProjectsPanel({
  open,
  refreshKey = 0,
  currentProjectId = null,
  currentVersionId = null,
  currentProjectName = null,
  cloudActionPending = false,
  onClose,
  onLoadVersion,
  onSaveCheckpoint,
  onCurrentProjectRenamed,
  onCurrentProjectDeleted,
  onCurrentVersionDeleted,
}) {
  const [projects, setProjects] = React.useState([]);
  const [nextCursor, setNextCursor] = React.useState(null);
  const [loadingProjects, setLoadingProjects] = React.useState(false);
  const [projectError, setProjectError] = React.useState("");
  const [selectedProject, setSelectedProject] = React.useState(null);
  const [versionsPanelOpen, setVersionsPanelOpen] = React.useState(false);
  const [versionsPanelMode, setVersionsPanelMode] = React.useState("history");
  const [versions, setVersions] = React.useState([]);
  const [loadingVersions, setLoadingVersions] = React.useState(false);
  const [versionError, setVersionError] = React.useState("");
  const [loadingVersionId, setLoadingVersionId] = React.useState("");
  const [confirmingProjectDeleteId, setConfirmingProjectDeleteId] = React.useState("");
  const [deletingProjectId, setDeletingProjectId] = React.useState("");
  const [confirmingVersionDeleteId, setConfirmingVersionDeleteId] = React.useState("");
  const [deletingVersionId, setDeletingVersionId] = React.useState("");
  const [renamingProjectId, setRenamingProjectId] = React.useState("");
  const [projectNameDraft, setProjectNameDraft] = React.useState("");
  const [savingProjectNameId, setSavingProjectNameId] = React.useState("");
  const [savingProjectCheckpointId, setSavingProjectCheckpointId] = React.useState("");
  const [renamingVersionId, setRenamingVersionId] = React.useState("");
  const [versionLabelDraft, setVersionLabelDraft] = React.useState("");
  const [savingVersionLabelId, setSavingVersionLabelId] = React.useState("");
  const [autosaveProjectsExpanded, setAutosaveProjectsExpanded] = React.useState(null);
  const [autosavesExpanded, setAutosavesExpanded] = React.useState(null);
  const [shareVersionId, setShareVersionId] = React.useState("");
  const [shareLinksByVersionId, setShareLinksByVersionId] = React.useState({});
  const [shareUrlsByLinkId, setShareUrlsByLinkId] = React.useState({});
  const [loadingShareVersionId, setLoadingShareVersionId] = React.useState("");
  const [creatingShareVersionId, setCreatingShareVersionId] = React.useState("");
  const [copyingShareLinkId, setCopyingShareLinkId] = React.useState("");
  const [confirmingShareLinkDeleteId, setConfirmingShareLinkDeleteId] = React.useState("");
  const [revokingShareLinkId, setRevokingShareLinkId] = React.useState("");
  const [shareError, setShareError] = React.useState("");
  const [shareErrorRole, setShareErrorRole] = React.useState(null);
  const [shareLinkRoleDraft, setShareLinkRoleDraft] = React.useState("viewer");

  const loadProjects = React.useCallback(
    async (cursor = null, { preserveSelection = false } = {}) => {
      setLoadingProjects(true);
      setProjectError("");
      try {
        const result = await listProjects({ limit: PAGE_SIZE, cursor: cursor ?? undefined });
        const projectsWithVersionKinds = await Promise.all(
          result.projects.map(async (project) => {
            try {
              const versionResult = await listVersions(project.id);
              const versionCounts = versionCountsFromVersions(versionResult.versions);
              return {
                ...project,
                ...versionCounts,
              };
            } catch {
              return project;
            }
          })
        );
        setProjects((current) => {
          const nextProjects = cursor
            ? [...current, ...projectsWithVersionKinds]
            : projectsWithVersionKinds;
          if (!cursor && preserveSelection) {
            setSelectedProject((selected) =>
              selected
                ? (nextProjects.find((project) => project.id === selected.id) ?? selected)
                : selected
            );
          }
          return nextProjects;
        });
        setNextCursor(result.nextCursor);
      } catch (error) {
        setProjectError(error instanceof Error ? error.message : "Cloud projects could not load.");
      } finally {
        setLoadingProjects(false);
      }
    },
    []
  );

  React.useEffect(() => {
    if (!open) return;
    setSelectedProject(null);
    setVersionsPanelOpen(false);
    setVersionsPanelMode("history");
    setVersions([]);
    setConfirmingProjectDeleteId("");
    setConfirmingVersionDeleteId("");
    setConfirmingShareLinkDeleteId("");
    setRenamingProjectId("");
    setRenamingVersionId("");
    setAutosaveProjectsExpanded(null);
    setAutosavesExpanded(null);
    setShareVersionId("");
    setShareLinksByVersionId({});
    setShareUrlsByLinkId({});
    setShareError("");
    setShareErrorRole(null);
    setShareLinkRoleDraft("viewer");
    void loadProjects(null);
  }, [loadProjects, open]);

  React.useEffect(() => {
    if (!shareErrorRole || shareErrorRole === shareLinkRoleDraft) return;
    setShareError("");
    setShareErrorRole(null);
  }, [shareErrorRole, shareLinkRoleDraft]);

  React.useEffect(() => {
    if (!open || refreshKey === 0) return;
    void loadProjects(null, { preserveSelection: true });
  }, [loadProjects, open, refreshKey]);

  React.useEffect(() => {
    if (!confirmingProjectDeleteId && !confirmingVersionDeleteId && !confirmingShareLinkDeleteId)
      return;

    const clearDeleteConfirmation = (event) => {
      if (event.target?.closest?.("[data-cloud-delete-confirm='true']")) return;
      setConfirmingProjectDeleteId("");
      setConfirmingVersionDeleteId("");
      setConfirmingShareLinkDeleteId("");
    };

    document.addEventListener("pointerdown", clearDeleteConfirmation, true);
    return () => document.removeEventListener("pointerdown", clearDeleteConfirmation, true);
  }, [confirmingProjectDeleteId, confirmingShareLinkDeleteId, confirmingVersionDeleteId]);

  const loadProjectVersions = React.useCallback(async (project) => {
    setSelectedProject(project);
    setVersions([]);
    setVersionError("");
    setAutosavesExpanded(null);
    setShareVersionId("");
    setShareError("");
    setShareErrorRole(null);
    setLoadingVersions(true);
    try {
      const result = await listVersions(project.id);
      const versionCounts = versionCountsFromVersions(result.versions);
      setVersions(result.versions);
      setProjects((current) =>
        current.map((entry) => (entry.id === project.id ? { ...entry, ...versionCounts } : entry))
      );
      setSelectedProject((current) =>
        current?.id === project.id
          ? { ...current, ...versionCounts }
          : { ...project, ...versionCounts }
      );
      return result.versions;
    } catch (error) {
      setVersionError(error instanceof Error ? error.message : "Cloud versions could not load.");
      return null;
    } finally {
      setLoadingVersions(false);
    }
  }, []);

  const handleProjectOpenLatest = React.useCallback(
    async (project) => {
      if (loadingVersionId || deletingVersionId) return;
      setLoadingVersionId(project.id);
      try {
        const loadedVersions = await loadProjectVersions(project);
        const latestVersion = loadedVersions?.[0];
        if (!latestVersion) return;
        await onLoadVersion?.(project.id, latestVersion.id, project);
        onClose?.();
      } finally {
        setLoadingVersionId("");
      }
    },
    [deletingVersionId, loadProjectVersions, loadingVersionId, onClose, onLoadVersion]
  );

  const handleProjectVersionsOpen = React.useCallback(
    async (project) => {
      setVersionsPanelOpen(true);
      setVersionsPanelMode("history");
      await loadProjectVersions(project);
    },
    [loadProjectVersions]
  );

  const handleProjectScroll = React.useCallback(
    (event) => {
      const el = event.currentTarget;
      if (!nextCursor || loadingProjects) return;
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 48) {
        void loadProjects(nextCursor);
      }
    },
    [loadProjects, loadingProjects, nextCursor]
  );

  const handleProjectDelete = React.useCallback(
    async (project) => {
      if (deletingProjectId) return;
      if (confirmingProjectDeleteId !== project.id) {
        setConfirmingProjectDeleteId(project.id);
        setConfirmingVersionDeleteId("");
        return;
      }

      setDeletingProjectId(project.id);
      setProjectError("");
      try {
        await deleteProject(project.id);
        setProjects((current) => current.filter((entry) => entry.id !== project.id));
        if (project.id === currentProjectId) {
          onCurrentProjectDeleted?.(project);
        }
        if (selectedProject?.id === project.id) {
          setSelectedProject(null);
          setVersionsPanelOpen(false);
          setVersionsPanelMode("history");
          setVersions([]);
        }
        setConfirmingProjectDeleteId("");
        setConfirmingShareLinkDeleteId("");
      } catch (error) {
        setProjectError(
          error instanceof Error ? error.message : "Cloud project could not be deleted."
        );
      } finally {
        setDeletingProjectId("");
      }
    },
    [
      confirmingProjectDeleteId,
      currentProjectId,
      deletingProjectId,
      onCurrentProjectDeleted,
      selectedProject,
    ]
  );

  const cancelProjectRename = React.useCallback(() => {
    setRenamingProjectId("");
    setProjectNameDraft("");
  }, []);

  const startProjectRename = React.useCallback((project) => {
    setRenamingProjectId(project.id);
    setProjectNameDraft(project.name || "Untitled Project");
    setConfirmingProjectDeleteId("");
    setConfirmingVersionDeleteId("");
  }, []);

  const commitProjectRename = React.useCallback(
    async (project) => {
      const name = projectNameDraft.trim();
      if (!name || name === project.name || savingProjectNameId) {
        cancelProjectRename();
        return;
      }

      setSavingProjectNameId(project.id);
      setProjectError("");
      try {
        const result = await patchProject(project.id, { name });
        setProjects((current) =>
          current.map((entry) =>
            entry.id === project.id ? { ...entry, ...result.project } : entry
          )
        );
        if (selectedProject?.id === project.id) {
          setSelectedProject((current) =>
            current ? { ...current, ...result.project } : result.project
          );
        }
        if (project.id === currentProjectId) {
          onCurrentProjectRenamed?.(result.project.name);
        }
        cancelProjectRename();
      } catch (error) {
        setProjectError(
          error instanceof Error ? error.message : "Cloud project could not be renamed."
        );
      } finally {
        setSavingProjectNameId("");
      }
    },
    [
      cancelProjectRename,
      currentProjectId,
      onCurrentProjectRenamed,
      projectNameDraft,
      savingProjectNameId,
      selectedProject,
    ]
  );

  const handleVersionLoad = React.useCallback(
    async (version) => {
      if (!selectedProject || loadingVersionId || deletingVersionId) return;
      setLoadingVersionId(version.id);
      try {
        await onLoadVersion?.(selectedProject.id, version.id, selectedProject);
        onClose?.();
      } finally {
        setLoadingVersionId("");
      }
    },
    [deletingVersionId, loadingVersionId, onClose, onLoadVersion, selectedProject]
  );

  const cancelVersionRename = React.useCallback(() => {
    setRenamingVersionId("");
    setVersionLabelDraft("");
  }, []);

  const startVersionRename = React.useCallback((version) => {
    setRenamingVersionId(version.id);
    setVersionLabelDraft(versionTitle(version));
    setConfirmingProjectDeleteId("");
    setConfirmingVersionDeleteId("");
  }, []);

  const commitVersionRename = React.useCallback(
    async (version) => {
      if (!selectedProject) return;
      const label = versionLabelDraft.trim();
      if (!label || (version.label && label === version.label) || savingVersionLabelId) {
        cancelVersionRename();
        return;
      }

      setSavingVersionLabelId(version.id);
      setVersionError("");
      try {
        const result = await labelVersion(selectedProject.id, version.id, label);
        setVersions((current) =>
          current.map((entry) => (entry.id === version.id ? result.version : entry))
        );
        cancelVersionRename();
      } catch (error) {
        setVersionError(
          error instanceof Error ? error.message : "Cloud version could not be renamed."
        );
      } finally {
        setSavingVersionLabelId("");
      }
    },
    [cancelVersionRename, savingVersionLabelId, selectedProject, versionLabelDraft]
  );

  const handleVersionDelete = React.useCallback(
    async (version) => {
      if (!selectedProject || deletingVersionId) return;
      if (confirmingVersionDeleteId !== version.id) {
        setConfirmingVersionDeleteId(version.id);
        setConfirmingProjectDeleteId("");
        return;
      }

      setDeletingVersionId(version.id);
      setVersionError("");
      try {
        await deleteVersion(selectedProject.id, version.id);
        setVersions((current) => current.filter((entry) => entry.id !== version.id));
        setShareLinksByVersionId((current) => {
          const { [version.id]: _deletedVersionLinks, ...remaining } = current;
          return remaining;
        });
        setShareUrlsByLinkId((current) => {
          const deletedLinkIds = new Set(
            (shareLinksByVersionId[version.id] || []).map((link) => link.id)
          );
          if (deletedLinkIds.size === 0) return current;
          return Object.fromEntries(
            Object.entries(current).filter(([linkId]) => !deletedLinkIds.has(linkId))
          );
        });
        if (selectedProject.id === currentProjectId && version.id === currentVersionId) {
          onCurrentVersionDeleted?.(version);
        }
        setConfirmingVersionDeleteId("");
      } catch (error) {
        setVersionError(
          error instanceof Error ? error.message : "Cloud version could not be deleted."
        );
      } finally {
        setDeletingVersionId("");
      }
    },
    [
      confirmingVersionDeleteId,
      currentProjectId,
      currentVersionId,
      deletingVersionId,
      onCurrentVersionDeleted,
      selectedProject,
      shareLinksByVersionId,
    ]
  );

  const shareUrlFromPath = React.useCallback((path) => {
    if (!path) return "";
    try {
      return new URL(path, window.location.origin).toString();
    } catch {
      return path;
    }
  }, []);

  const embedUrlFromPath = React.useCallback((path) => {
    if (!path) return "";
    try {
      return new URL(path, embedOriginForCurrentPage()).toString();
    } catch {
      return path;
    }
  }, []);

  const loadShareLinksForVersion = React.useCallback(
    async (version, projectOverride = null) => {
      const project = projectOverride || selectedProject;
      if (!project) return [];
      setLoadingShareVersionId(version.id);
      setShareError("");
      setShareErrorRole(null);
      try {
        const [shareResult, embedResult] = await Promise.all([
          listVersionShareLinks(project.id, version.id, { kind: "share" }),
          listVersionEmbedLinks(project.id, version.id),
        ]);
        const links = uniqueShareLinksById([...shareResult.shareLinks, ...embedResult.shareLinks]);
        setShareLinksByVersionId((current) => ({
          ...current,
          [version.id]: links,
        }));
        return links;
      } catch (error) {
        setShareError(error instanceof Error ? error.message : "Share links could not be loaded.");
        setShareErrorRole(null);
        return [];
      } finally {
        setLoadingShareVersionId("");
      }
    },
    [selectedProject]
  );

  const toggleSharePanel = React.useCallback(
    (version) => {
      setConfirmingProjectDeleteId("");
      setConfirmingVersionDeleteId("");
      setConfirmingShareLinkDeleteId("");
      setRenamingVersionId("");
      setShareError("");
      setShareErrorRole(null);
      setVersionsPanelMode("history");
      if (shareVersionId === version.id) {
        setShareVersionId("");
        return;
      }
      setShareVersionId(version.id);
      void loadShareLinksForVersion(version);
    },
    [loadShareLinksForVersion, shareVersionId]
  );

  const copyShareUrl = React.useCallback(async (linkId, url, role = "viewer") => {
    if (!url) return;
    setCopyingShareLinkId(linkId);
    try {
      const copied = await copyTextToClipboard(url, "[CloudProjectsPanel]");
      if (copied) {
        dispatchCloudToast(SHARE_LINK_ROLES[role].copiedToast, "success");
      } else {
        dispatchCloudToast("Clipboard access is unavailable. Copy the link manually.", "warning");
      }
    } finally {
      setCopyingShareLinkId("");
    }
  }, []);

  const copyEmbedCode = React.useCallback(async (linkId, url) => {
    const code = embedIframeCode(url);
    if (!code) return;
    setCopyingShareLinkId(linkId);
    try {
      const copied = await copyTextToClipboard(code, "[CloudProjectsPanel]");
      if (copied) {
        dispatchCloudToast("Embed iframe code copied.", "success");
      } else {
        dispatchCloudToast(
          "Clipboard access is unavailable. Copy the iframe code manually.",
          "warning"
        );
      }
    } finally {
      setCopyingShareLinkId("");
    }
  }, []);

  const createShareLinkForVersion = React.useCallback(
    async (project, version, role = "viewer") => {
      const result = await createVersionShareLink(project.id, version.id, { role });
      const url = shareUrlFromPath(result.shareLink.path);
      setShareUrlsByLinkId((current) => ({
        ...current,
        [result.shareLink.id]: url,
      }));
      setShareLinksByVersionId((current) => ({
        ...current,
        [version.id]: uniqueShareLinksById([result.shareLink, ...(current[version.id] || [])]),
      }));
      await copyShareUrl(result.shareLink.id, url, role);
    },
    [copyShareUrl, shareUrlFromPath]
  );

  const createEmbedLinkForVersion = React.useCallback(
    async (project, version) => {
      const result = await createVersionEmbedLink(project.id, version.id);
      const embedPath = embedPathForLink(result.shareLink);
      if (!embedPath) {
        throw new Error("Embed link could not be created. The API returned a share link.");
      }
      const url = embedUrlFromPath(embedPath);
      setShareUrlsByLinkId((current) => ({
        ...current,
        [result.shareLink.id]: url,
      }));
      setShareLinksByVersionId((current) => ({
        ...current,
        [version.id]: uniqueShareLinksById([
          { ...result.shareLink, path: embedPath },
          ...(current[version.id] || []),
        ]),
      }));
      await copyEmbedCode(result.shareLink.id, url);
    },
    [copyEmbedCode, embedUrlFromPath]
  );

  const handleShareProjectVersion = React.useCallback(
    async (project) => {
      if (!project || project.isOwner === false || creatingShareVersionId) {
        return;
      }

      const activeSceneVersionId = project.id === currentProjectId ? currentVersionId : null;
      setCreatingShareVersionId(activeSceneVersionId || project.id);
      setVersionsPanelOpen(true);
      setVersionsPanelMode("share");
      setShareError("");
      setShareErrorRole(null);
      setConfirmingProjectDeleteId("");
      setConfirmingVersionDeleteId("");
      setConfirmingShareLinkDeleteId("");
      try {
        const loadedVersions = await loadProjectVersions(project);
        const versionId = activeSceneVersionId || loadedVersions?.[0]?.id || null;

        if (!versionId) {
          dispatchCloudToast("This cloud project has no versions to share.", "warning");
          return;
        }

        const targetVersion = loadedVersions?.find((version) => version.id === versionId) || {
          id: versionId,
        };
        setShareVersionId(versionId);
        if (targetVersion.kind === "autosave") {
          setAutosavesExpanded(true);
        }
        const shareLinks = await loadShareLinksForVersion(targetVersion, project);
        const existingLink = activeShareLinkForRole(shareLinks, "viewer");
        if (existingLink) {
          const url = shareUrlsByLinkId[existingLink.id] || shareUrlFromPath(existingLink.path);
          if (url) {
            setShareUrlsByLinkId((current) => ({
              ...current,
              [existingLink.id]: url,
            }));
            await copyShareUrl(existingLink.id, url, "viewer");
          } else {
            const message =
              "A read-only link is already active. Delete it before creating a new one.";
            setShareError(message);
            setShareErrorRole("viewer");
          }
          return;
        }

        setCreatingShareVersionId(versionId);
        await createShareLinkForVersion(project, targetVersion, "viewer");
      } catch (error) {
        setShareError(error instanceof Error ? error.message : "Share link could not be created.");
        setShareErrorRole("viewer");
        dispatchCloudToast("Share link could not be created.", "error");
      } finally {
        setCreatingShareVersionId("");
      }
    },
    [
      copyShareUrl,
      creatingShareVersionId,
      createShareLinkForVersion,
      currentProjectId,
      currentVersionId,
      loadProjectVersions,
      loadShareLinksForVersion,
      shareUrlsByLinkId,
      shareUrlFromPath,
    ]
  );

  const handleCreateShareLink = React.useCallback(
    async (version, projectOverride = null) => {
      const project = projectOverride || selectedProject;
      if (!project || creatingShareVersionId) return;
      setShareError("");
      setShareErrorRole(null);
      const activeLink = activeShareLinkForRole(
        shareLinksByVersionId[version.id] || [],
        shareLinkRoleDraft
      );
      if (activeLink) {
        const url = shareUrlsByLinkId[activeLink.id] || shareUrlFromPath(activeLink.path);
        if (url) {
          await copyShareUrl(activeLink.id, url, shareLinkRoleDraft);
          return;
        }
        setShareError(
          `An active ${SHARE_LINK_ROLES[shareLinkRoleDraft].label.toLowerCase()} link already exists. Delete it before creating a new one.`
        );
        setShareErrorRole(shareLinkRoleDraft);
        return;
      }
      setCreatingShareVersionId(version.id);
      try {
        await createShareLinkForVersion(project, version, shareLinkRoleDraft);
      } catch (error) {
        setShareError(error instanceof Error ? error.message : "Share link could not be created.");
        setShareErrorRole(shareLinkRoleDraft);
      } finally {
        setCreatingShareVersionId("");
      }
    },
    [
      copyShareUrl,
      createShareLinkForVersion,
      creatingShareVersionId,
      selectedProject,
      shareLinkRoleDraft,
      shareLinksByVersionId,
      shareUrlsByLinkId,
      shareUrlFromPath,
    ]
  );

  const handleCreateEmbedLink = React.useCallback(
    async (version, projectOverride = null) => {
      const project = projectOverride || selectedProject;
      if (!project || creatingShareVersionId) return;
      setShareError("");
      setShareErrorRole(null);
      const existingLink = activeEmbedLink(shareLinksByVersionId[version.id] || []);
      if (existingLink) {
        const url =
          shareUrlsByLinkId[existingLink.id] || embedUrlFromPath(embedPathForLink(existingLink));
        if (url) {
          await copyEmbedCode(existingLink.id, url);
          return;
        }
        setShareError("An active embed already exists. Delete it before creating a new one.");
        return;
      }
      setCreatingShareVersionId(version.id);
      try {
        await createEmbedLinkForVersion(project, version);
      } catch (error) {
        setShareError(error instanceof Error ? error.message : "Embed link could not be created.");
      } finally {
        setCreatingShareVersionId("");
      }
    },
    [
      copyEmbedCode,
      createEmbedLinkForVersion,
      creatingShareVersionId,
      selectedProject,
      shareLinksByVersionId,
      shareUrlsByLinkId,
      embedUrlFromPath,
    ]
  );

  const handleRevokeShareLink = React.useCallback(
    async (version, link) => {
      if (!selectedProject || revokingShareLinkId) return;
      if (confirmingShareLinkDeleteId !== link.id) {
        setConfirmingShareLinkDeleteId(link.id);
        setConfirmingProjectDeleteId("");
        setConfirmingVersionDeleteId("");
        return;
      }
      setRevokingShareLinkId(link.id);
      setShareError("");
      setShareErrorRole(null);
      try {
        await revokeVersionShareLink(selectedProject.id, version.id, link.id);
        await loadShareLinksForVersion(version);
        setConfirmingShareLinkDeleteId("");
        dispatchCloudToast("Share link deleted.", "success");
      } catch (error) {
        setShareError(error instanceof Error ? error.message : "Share link could not be deleted.");
        setShareErrorRole(null);
      } finally {
        setRevokingShareLinkId("");
      }
    },
    [confirmingShareLinkDeleteId, loadShareLinksForVersion, revokingShareLinkId, selectedProject]
  );

  const manualVersions = React.useMemo(
    () => versions.filter((version) => version.kind !== "autosave"),
    [versions]
  );
  const autosaveVersions = React.useMemo(
    () => versions.filter((version) => version.kind === "autosave"),
    [versions]
  );

  const manualProjects = React.useMemo(
    () => projects.filter((project) => !isAutosaveOnlyProject(project)),
    [projects]
  );
  const autosaveProjects = React.useMemo(
    () => projects.filter((project) => isAutosaveOnlyProject(project)),
    [projects]
  );
  const defaultShowAutosaveProjects = autosaveProjects.length > 0;
  const showAutosaveVersions =
    autosavesExpanded ?? (manualVersions.length === 0 && autosaveVersions.length > 0);
  const showAutosaveProjects = autosaveProjectsExpanded ?? defaultShowAutosaveProjects;
  const handleSaveProjectCheckpoint = React.useCallback(
    async (project) => {
      if (
        !project ||
        project.id !== currentProjectId ||
        project.isOwner === false ||
        cloudActionPending ||
        savingProjectCheckpointId
      ) {
        return;
      }

      setSavingProjectCheckpointId(project.id);
      setConfirmingProjectDeleteId("");
      setConfirmingVersionDeleteId("");
      setRenamingProjectId("");
      setRenamingVersionId("");
      setSelectedProject(project);
      setVersionError("");
      try {
        const saved = await onSaveCheckpoint?.(project.name || currentProjectName || "Cloud save");
        if (!saved) return;
        await loadProjects(null, { preserveSelection: true });
        setLoadingVersions(true);
        const result = await listVersions(project.id);
        setVersions(result.versions);
        const versionCounts = versionCountsFromVersions(result.versions);
        setProjects((current) =>
          current.map((project) =>
            project.id === currentProjectId ? { ...project, ...versionCounts } : project
          )
        );
        setSelectedProject((current) =>
          current?.id === project.id
            ? { ...current, ...versionCounts }
            : { ...project, ...versionCounts }
        );
      } catch (error) {
        setVersionError(error instanceof Error ? error.message : "Cloud versions could not load.");
      } finally {
        setLoadingVersions(false);
        setSavingProjectCheckpointId("");
      }
    },
    [
      cloudActionPending,
      currentProjectId,
      currentProjectName,
      loadProjects,
      onSaveCheckpoint,
      savingProjectCheckpointId,
    ]
  );

  const renderShareLinkPanel = React.useCallback(
    (version) => {
      const shareLinks = uniqueShareLinksById(shareLinksByVersionId[version.id] || []);
      const activeShareLinks = shareLinks.filter(
        (link) => !link.revokedAt && link.kind !== "embed"
      );
      const activeEmbedLinks = shareLinks.filter(
        (link) => !link.revokedAt && link.kind === "embed"
      );
      const selectedRoleActiveLink = activeShareLinkForRole(activeShareLinks, shareLinkRoleDraft);
      const selectedEmbedActiveLink = activeEmbedLink(activeEmbedLinks);
      const selectedRoleActiveUrl = selectedRoleActiveLink
        ? shareUrlsByLinkId[selectedRoleActiveLink.id] ||
          shareUrlFromPath(selectedRoleActiveLink.path)
        : "";
      const selectedEmbedActiveUrl = selectedEmbedActiveLink
        ? shareUrlsByLinkId[selectedEmbedActiveLink.id] ||
          embedUrlFromPath(embedPathForLink(selectedEmbedActiveLink))
        : "";
      const createButtonLabel =
        creatingShareVersionId === version.id
          ? "Creating..."
          : selectedRoleActiveLink
            ? selectedRoleActiveUrl
              ? "Copy"
              : "Create"
            : "Create";
      const embedButtonLabel =
        creatingShareVersionId === version.id
          ? "Creating..."
          : selectedEmbedActiveLink && selectedEmbedActiveUrl
            ? "Copy iframe"
            : "Create iframe";

      return (
        <div className="rounded border border-gray-700 bg-gray-900/40 p-3 text-xs text-gray-300">
          <div className="space-y-3">
            <div>
              <div className="text-sm font-medium text-gray-100">Share link</div>
              <div className="mt-0.5 text-gray-400">
                Create a link for this saved version. Existing links stay active until revoked.
              </div>
            </div>

            <div className="rounded border border-gray-800 bg-black/15 p-2">
              <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                New link access
              </div>
              <div className="flex min-w-0 items-center gap-2">
                <MenuSelect
                  value={shareLinkRoleDraft}
                  onChange={setShareLinkRoleDraft}
                  options={SHARE_LINK_ROLE_OPTIONS}
                  size="compact"
                  className="min-w-0 flex-1"
                  triggerClassName="min-h-8 bg-gray-950/80 text-sm"
                  contentClassName="w-64"
                  aria-label="Share link access"
                />
                <button
                  type="button"
                  className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded border border-gray-600 px-2.5 font-medium text-gray-100 transition-colors hover:border-gray-400 hover:bg-gray-800/60 focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-500 disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={
                    creatingShareVersionId === version.id ||
                    Boolean(selectedRoleActiveLink && !selectedRoleActiveUrl)
                  }
                  onClick={() => void handleCreateShareLink(version)}
                >
                  {selectedRoleActiveUrl ? (
                    <Copy className="h-3.5 w-3.5" aria-hidden />
                  ) : (
                    <Share2 className="h-3.5 w-3.5" aria-hidden />
                  )}
                  {createButtonLabel}
                </button>
              </div>
              <p className="mt-2 leading-5 text-gray-400">
                {SHARE_LINK_ROLES[shareLinkRoleDraft].description}
              </p>
            </div>
            <div className="rounded border border-gray-800 bg-black/15 p-2">
              <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                Embed
              </div>
              <p className="leading-5 text-gray-400">
                Embeds are public read-only viewers. Anyone with the iframe code can view this saved
                version.
              </p>
              <button
                type="button"
                className="mt-2 inline-flex h-8 items-center gap-1.5 rounded border border-gray-600 px-2.5 font-medium text-gray-100 transition-colors hover:border-gray-400 hover:bg-gray-800/60 focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-500 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={
                  creatingShareVersionId === version.id ||
                  Boolean(selectedEmbedActiveLink && !selectedEmbedActiveUrl)
                }
                onClick={() => void handleCreateEmbedLink(version)}
              >
                {selectedEmbedActiveUrl ? (
                  <Copy className="h-3.5 w-3.5" aria-hidden />
                ) : (
                  <Share2 className="h-3.5 w-3.5" aria-hidden />
                )}
                {embedButtonLabel}
              </button>
              {selectedEmbedActiveUrl ? (
                <div className="mt-2 truncate rounded border border-gray-800 bg-gray-950/70 px-2 py-1.5 font-mono text-[11px] text-gray-300">
                  {embedIframeCode(selectedEmbedActiveUrl)}
                </div>
              ) : null}
            </div>
          </div>
          {shareError && (!shareErrorRole || shareErrorRole === shareLinkRoleDraft) ? (
            <p className="mt-3 text-red-300">{shareError}</p>
          ) : null}
          {loadingShareVersionId === version.id ? (
            <p className="mt-3 text-gray-400">Loading...</p>
          ) : null}
          {activeShareLinks.length > 0 ? (
            <div className="mt-3 space-y-2">
              <div className="flex items-center justify-between text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                <span>Active links</span>
                <span>{activeShareLinks.length}</span>
              </div>
              {activeShareLinks.map((link) => {
                const url = shareUrlsByLinkId[link.id] || "";
                const role = shareLinkRole(link);
                return (
                  <div key={link.id} className="rounded border border-gray-800 bg-black/15 p-2">
                    <div className="flex min-w-0 items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex min-w-0 items-center gap-2">
                          <span className="shrink-0 rounded border border-gray-700 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-300">
                            {SHARE_LINK_ROLES[role].label}
                          </span>
                          <span className="min-w-0 truncate text-gray-400">
                            {formatDate(link.createdAt)}
                          </span>
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        {url ? (
                          <button
                            type="button"
                            className={ROW_ACTION_BUTTON_CLASS}
                            disabled={copyingShareLinkId === link.id}
                            aria-label={`Copy ${SHARE_LINK_ROLES[role].label.toLowerCase()} share link`}
                            onClick={() => void copyShareUrl(link.id, url, role)}
                          >
                            <Copy className="h-4 w-4" aria-hidden />
                          </button>
                        ) : null}
                        <button
                          type="button"
                          data-cloud-delete-confirm={
                            confirmingShareLinkDeleteId === link.id ? "true" : undefined
                          }
                          className={
                            confirmingShareLinkDeleteId === link.id
                              ? ROW_DELETE_CONFIRM_CLASS
                              : `${ROW_ACTION_BUTTON_CLASS} hover:text-red-400`
                          }
                          disabled={revokingShareLinkId === link.id}
                          aria-label={`Delete ${SHARE_LINK_ROLES[role].label.toLowerCase()} share link`}
                          onClick={() => void handleRevokeShareLink(version, link)}
                        >
                          {confirmingShareLinkDeleteId === link.id ? (
                            revokingShareLinkId === link.id ? (
                              "Deleting..."
                            ) : (
                              "Confirm"
                            )
                          ) : (
                            <Trash2 className="h-4 w-4" aria-hidden />
                          )}
                        </button>
                      </div>
                    </div>
                    {url ? (
                      <div className="mt-2 truncate rounded border border-gray-800 bg-gray-950/70 px-2 py-1.5 font-mono text-[11px] text-gray-300">
                        {url}
                      </div>
                    ) : (
                      <div className="mt-2 text-[11px] text-gray-500">
                        Create a new link if you need another copy.
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : !loadingShareVersionId ? (
            <p className="mt-3 rounded border border-dashed border-gray-800 px-2 py-2 text-gray-500">
              {creatingShareVersionId === version.id
                ? "Creating share link..."
                : "No active share links."}
            </p>
          ) : null}
          {activeEmbedLinks.length > 0 ? (
            <div className="mt-3 space-y-2 border-t border-gray-800 pt-3">
              <div className="flex items-center justify-between text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                <span>Active embeds</span>
                <span>{activeEmbedLinks.length}</span>
              </div>
              {activeEmbedLinks.map((link) => {
                const url = shareUrlsByLinkId[link.id] || embedUrlFromPath(embedPathForLink(link));
                return (
                  <div key={link.id} className="rounded border border-gray-800 bg-black/15 p-2">
                    <div className="flex min-w-0 items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex min-w-0 items-center gap-2">
                          <span className="shrink-0 rounded border border-gray-700 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-300">
                            Embed
                          </span>
                          <span className="min-w-0 truncate text-gray-400">
                            {formatDate(link.createdAt)}
                          </span>
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        {url ? (
                          <button
                            type="button"
                            className={ROW_ACTION_BUTTON_CLASS}
                            disabled={copyingShareLinkId === link.id}
                            aria-label="Copy embed iframe code"
                            onClick={() => void copyEmbedCode(link.id, url)}
                          >
                            <Copy className="h-4 w-4" aria-hidden />
                          </button>
                        ) : null}
                        <button
                          type="button"
                          data-cloud-delete-confirm={
                            confirmingShareLinkDeleteId === link.id ? "true" : undefined
                          }
                          className={
                            confirmingShareLinkDeleteId === link.id
                              ? ROW_DELETE_CONFIRM_CLASS
                              : `${ROW_ACTION_BUTTON_CLASS} hover:text-red-400`
                          }
                          disabled={revokingShareLinkId === link.id}
                          aria-label="Delete embed link"
                          onClick={() => void handleRevokeShareLink(version, link)}
                        >
                          {confirmingShareLinkDeleteId === link.id ? (
                            revokingShareLinkId === link.id ? (
                              "Deleting..."
                            ) : (
                              "Confirm"
                            )
                          ) : (
                            <Trash2 className="h-4 w-4" aria-hidden />
                          )}
                        </button>
                      </div>
                    </div>
                    {url ? (
                      <div className="mt-2 truncate rounded border border-gray-800 bg-gray-950/70 px-2 py-1.5 font-mono text-[11px] text-gray-300">
                        {embedIframeCode(url)}
                      </div>
                    ) : (
                      <div className="mt-2 text-[11px] text-gray-500">
                        Create a new embed if you need another copy.
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
      );
    },
    [
      confirmingShareLinkDeleteId,
      copyEmbedCode,
      copyingShareLinkId,
      copyShareUrl,
      creatingShareVersionId,
      handleCreateEmbedLink,
      handleCreateShareLink,
      handleRevokeShareLink,
      loadingShareVersionId,
      revokingShareLinkId,
      shareError,
      shareErrorRole,
      shareLinksByVersionId,
      shareLinkRoleDraft,
      shareUrlsByLinkId,
      embedUrlFromPath,
      shareUrlFromPath,
    ]
  );

  const renderProjectRow = React.useCallback(
    (project) => {
      const canManageProject = project.isOwner !== false;
      const isCurrentProject = project.id === currentProjectId;
      const canSaveProjectCheckpoint = canManageProject && isCurrentProject && !cloudActionPending;
      const knownVersionCount =
        Number.isFinite(project.autosaveVersionCount) || Number.isFinite(project.manualVersionCount)
          ? (project.autosaveVersionCount ?? 0) + (project.manualVersionCount ?? 0)
          : null;
      const canShareProject =
        canManageProject &&
        !creatingShareVersionId &&
        (knownVersionCount !== 0 || (isCurrentProject && Boolean(currentVersionId)));
      return (
        <div
          key={project.id}
          className={`menu-button mb-2 flex min-w-0 items-center gap-1 px-2 py-2 ${
            selectedProject?.id === project.id ? "cloud-project-row-selected" : ""
          } ${isCurrentProject ? "cloud-project-row-current" : ""}`}
        >
          {renamingProjectId === project.id ? (
            <>
              <input
                type="text"
                autoFocus
                value={projectNameDraft}
                onChange={(event) => setProjectNameDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    void commitProjectRename(project);
                  } else if (event.key === "Escape") {
                    event.preventDefault();
                    cancelProjectRename();
                  }
                }}
                className="min-w-0 flex-1 rounded border border-gray-600 bg-gray-900/90 px-2 py-1 text-sm text-gray-100 outline-none focus:border-gray-400"
                aria-label="Cloud project name"
              />
              <button
                type="button"
                className="shrink-0 rounded p-1 text-gray-300 transition-colors hover:text-green-300"
                disabled={savingProjectNameId === project.id}
                aria-label="Save project name"
                onClick={() => void commitProjectRename(project)}
              >
                <Check className="h-3.5 w-3.5" aria-hidden />
              </button>
              <button
                type="button"
                className="shrink-0 rounded p-1 text-gray-300 transition-colors hover:text-gray-100"
                disabled={savingProjectNameId === project.id}
                aria-label="Cancel project rename"
                onClick={cancelProjectRename}
              >
                <X className="h-3.5 w-3.5" aria-hidden />
              </button>
            </>
          ) : (
            <button
              type="button"
              className="flex min-w-0 flex-1 flex-col items-start gap-1 rounded px-1 py-0.5 text-left transition-colors hover:text-gray-100 focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-500"
              disabled={Boolean(loadingVersionId || deletingVersionId)}
              onClick={() => void handleProjectOpenLatest(project)}
            >
              <span className="max-w-full truncate font-medium text-gray-100">
                {project.name || "Untitled Project"}
              </span>
              <span className="flex max-w-full items-center gap-2 text-xs text-gray-400">
                <span className="whitespace-nowrap">{formatDate(project.updatedAt)}</span>
                {!canManageProject ? (
                  <span className="whitespace-nowrap rounded border border-gray-600 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-gray-300">
                    {project.accessRole === "editor" ? "Editor" : "Viewer"}
                  </span>
                ) : null}
                {isCurrentProject ? (
                  <span className="whitespace-nowrap rounded border border-emerald-500/70 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-emerald-200">
                    Editing
                  </span>
                ) : null}
              </span>
            </button>
          )}
          <Tooltip content="View checkpoints and autosaves">
            <button
              type="button"
              className={`${ROW_ACTION_BUTTON_CLASS} ${
                versionsPanelOpen &&
                versionsPanelMode === "history" &&
                selectedProject?.id === project.id
                  ? "text-gray-100"
                  : ""
              }`}
              disabled={Boolean(loadingVersionId || deletingVersionId || renamingProjectId)}
              aria-label="View checkpoints and autosaves"
              onClick={() => void handleProjectVersionsOpen(project)}
            >
              <History className="h-4 w-4" aria-hidden />
            </button>
          </Tooltip>
          {canManageProject ? (
            <>
              <Tooltip
                content={
                  isCurrentProject ? "Share current cloud version" : "Share latest cloud version"
                }
              >
                <button
                  type="button"
                  className={ROW_ACTION_BUTTON_CLASS}
                  disabled={
                    !canShareProject ||
                    Boolean(loadingVersionId || deletingProjectId || renamingProjectId)
                  }
                  aria-label={
                    isCurrentProject ? "Share current cloud version" : "Share latest cloud version"
                  }
                  onClick={() => void handleShareProjectVersion(project)}
                >
                  <Share2 className="h-4 w-4" aria-hidden />
                </button>
              </Tooltip>
              {isCurrentProject ? (
                <button
                  type="button"
                  className={ROW_ACTION_BUTTON_CLASS}
                  disabled={
                    !canSaveProjectCheckpoint ||
                    Boolean(savingProjectCheckpointId || deletingProjectId || renamingProjectId)
                  }
                  aria-label="Save cloud project checkpoint"
                  onClick={() => void handleSaveProjectCheckpoint(project)}
                >
                  <Save className="h-4 w-4" aria-hidden />
                </button>
              ) : null}
              <button
                type="button"
                className={ROW_ACTION_BUTTON_CLASS}
                disabled={Boolean(deletingProjectId || renamingProjectId)}
                aria-label="Rename cloud project"
                onClick={() => startProjectRename(project)}
              >
                <Pencil className="h-4 w-4" aria-hidden />
              </button>
              <button
                type="button"
                data-cloud-delete-confirm={
                  confirmingProjectDeleteId === project.id ? "true" : undefined
                }
                className={
                  confirmingProjectDeleteId === project.id
                    ? ROW_DELETE_CONFIRM_CLASS
                    : `${ROW_ACTION_BUTTON_CLASS} hover:text-red-400`
                }
                disabled={Boolean(deletingProjectId || renamingProjectId)}
                aria-label="Delete cloud project"
                onClick={() => void handleProjectDelete(project)}
              >
                {confirmingProjectDeleteId === project.id ? (
                  deletingProjectId === project.id ? (
                    "Deleting..."
                  ) : (
                    "Confirm"
                  )
                ) : (
                  <Trash2 className="h-4 w-4" aria-hidden />
                )}
              </button>
            </>
          ) : null}
        </div>
      );
    },
    [
      cancelProjectRename,
      cloudActionPending,
      commitProjectRename,
      confirmingProjectDeleteId,
      currentProjectId,
      currentVersionId,
      creatingShareVersionId,
      deletingProjectId,
      deletingVersionId,
      handleProjectDelete,
      handleProjectOpenLatest,
      handleProjectVersionsOpen,
      handleShareProjectVersion,
      handleSaveProjectCheckpoint,
      loadingVersionId,
      projectNameDraft,
      renamingProjectId,
      savingProjectCheckpointId,
      savingProjectNameId,
      selectedProject?.id,
      startProjectRename,
      versionsPanelOpen,
      versionsPanelMode,
    ]
  );

  const renderVersionRow = React.useCallback(
    (version) => {
      const canManageVersions = selectedProject?.isOwner !== false;
      const isCurrentVersion =
        selectedProject?.id === currentProjectId && version.id === currentVersionId;
      return (
        <React.Fragment key={version.id}>
          <div
            className={`menu-button mb-2 flex min-w-0 items-center gap-1 px-2 py-2 ${
              isCurrentVersion ? "cloud-project-row-current" : ""
            }`}
          >
            {renamingVersionId === version.id ? (
              <>
                <input
                  type="text"
                  autoFocus
                  value={versionLabelDraft}
                  onChange={(event) => setVersionLabelDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      void commitVersionRename(version);
                    } else if (event.key === "Escape") {
                      event.preventDefault();
                      cancelVersionRename();
                    }
                  }}
                  className="min-w-0 flex-1 rounded border border-gray-600 bg-gray-900/90 px-2 py-1 text-sm text-gray-100 outline-none focus:border-gray-400"
                  aria-label="Cloud version name"
                />
                <button
                  type="button"
                  className="shrink-0 rounded p-1 text-gray-300 transition-colors hover:text-green-300"
                  disabled={savingVersionLabelId === version.id}
                  aria-label="Save version name"
                  onClick={() => void commitVersionRename(version)}
                >
                  <Check className="h-3.5 w-3.5" aria-hidden />
                </button>
                <button
                  type="button"
                  className="shrink-0 rounded p-1 text-gray-300 transition-colors hover:text-gray-100"
                  disabled={savingVersionLabelId === version.id}
                  aria-label="Cancel version rename"
                  onClick={cancelVersionRename}
                >
                  <X className="h-3.5 w-3.5" aria-hidden />
                </button>
              </>
            ) : (
              <button
                type="button"
                className="flex min-w-0 flex-1 flex-col items-start gap-1 rounded px-1 py-0.5 text-left transition-colors hover:text-gray-100 focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-500"
                disabled={Boolean(loadingVersionId || deletingVersionId)}
                onClick={() => void handleVersionLoad(version)}
              >
                <span className="flex max-w-full min-w-0 items-center gap-2">
                  <span className="truncate font-medium text-gray-100">
                    {versionTitle(version)}
                  </span>
                  {isCurrentVersion ? (
                    <span className="shrink-0 whitespace-nowrap rounded border border-emerald-500/70 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-emerald-200">
                      Editing
                    </span>
                  ) : null}
                </span>
                <span className="text-xs text-gray-400">
                  #{version.versionNumber} - {formatDate(version.createdAt)} -{" "}
                  {formatBytes(version.metadataSize)}
                  {loadingVersionId === version.id ? " - Opening..." : ""}
                </span>
              </button>
            )}
            {canManageVersions ? (
              <>
                <button
                  type="button"
                  className={`${ROW_ACTION_BUTTON_CLASS} ${
                    shareVersionId === version.id ? "text-gray-100" : ""
                  }`}
                  disabled={Boolean(loadingVersionId || deletingVersionId || renamingVersionId)}
                  aria-label="Share read-only cloud version link"
                  aria-expanded={shareVersionId === version.id}
                  onClick={() => toggleSharePanel(version)}
                >
                  <Share2 className="h-4 w-4" aria-hidden />
                </button>
                <button
                  type="button"
                  className={ROW_ACTION_BUTTON_CLASS}
                  disabled={Boolean(loadingVersionId || deletingVersionId || renamingVersionId)}
                  aria-label="Rename cloud version"
                  onClick={() => startVersionRename(version)}
                >
                  <Pencil className="h-4 w-4" aria-hidden />
                </button>
                <button
                  type="button"
                  data-cloud-delete-confirm={
                    confirmingVersionDeleteId === version.id ? "true" : undefined
                  }
                  className={
                    confirmingVersionDeleteId === version.id
                      ? ROW_DELETE_CONFIRM_CLASS
                      : `${ROW_ACTION_BUTTON_CLASS} hover:text-red-400`
                  }
                  disabled={Boolean(loadingVersionId || deletingVersionId || renamingVersionId)}
                  aria-label={
                    isCurrentVersion ? "Delete current cloud version" : "Delete cloud version"
                  }
                  onClick={() => void handleVersionDelete(version)}
                >
                  {confirmingVersionDeleteId === version.id ? (
                    deletingVersionId === version.id ? (
                      "Deleting..."
                    ) : isCurrentVersion ? (
                      "Delete current"
                    ) : (
                      "Confirm"
                    )
                  ) : (
                    <Trash2 className="h-4 w-4" aria-hidden />
                  )}
                </button>
              </>
            ) : null}
          </div>
          {shareVersionId === version.id ? (
            <div className="mb-3">{renderShareLinkPanel(version)}</div>
          ) : null}
        </React.Fragment>
      );
    },
    [
      cancelVersionRename,
      commitVersionRename,
      confirmingVersionDeleteId,
      currentProjectId,
      currentVersionId,
      deletingVersionId,
      handleVersionDelete,
      handleVersionLoad,
      loadingVersionId,
      renamingVersionId,
      renderShareLinkPanel,
      savingVersionLabelId,
      selectedProject?.id,
      selectedProject?.isOwner,
      shareVersionId,
      startVersionRename,
      toggleSharePanel,
      versionLabelDraft,
    ]
  );

  const focusedShareVersion = React.useMemo(
    () => versions.find((version) => version.id === shareVersionId) || null,
    [shareVersionId, versions]
  );

  return (
    <DraggablePanel
      open={open}
      onClose={onClose}
      title="Cloud Projects"
      titleIcon={Cloud}
      initialSize={{ width: 560, height: 560 }}
      minWidth={360}
      minHeight={380}
    >
      {currentProjectId ? (
        <div className="border-b border-gray-700 px-3 py-2 text-xs text-gray-300">
          <span className="font-medium text-emerald-200">Editing:</span>{" "}
          <span className="text-gray-100">{currentProjectName || "Cloud project"}</span>
        </div>
      ) : null}
      <div
        className={`grid min-h-0 flex-1 gap-0 overflow-hidden text-sm text-gray-200 ${
          versionsPanelOpen ? "grid-cols-[minmax(0,1fr)_minmax(0,1fr)]" : "grid-cols-1"
        }`}
      >
        <section
          className={`flex min-h-0 flex-col ${versionsPanelOpen ? "border-r border-gray-700" : ""}`}
        >
          <div className="border-b border-gray-700 px-3 py-2 text-xs font-medium uppercase tracking-wide text-gray-400">
            Projects
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-2" onScroll={handleProjectScroll}>
            {manualProjects.length > 0 ? (
              <div className="mb-1 flex items-center justify-between px-2 text-[10px] font-semibold uppercase tracking-wide text-gray-300">
                <span>Saved projects</span>
                <span className="text-gray-400">{manualProjects.length}</span>
              </div>
            ) : null}
            {manualProjects.map(renderProjectRow)}
            {autosaveProjects.length > 0 ? (
              <div
                className={
                  manualProjects.length > 0
                    ? "mt-3 border-t border-gray-800 pt-2"
                    : "border-t border-gray-800 pt-2"
                }
              >
                <button
                  type="button"
                  className="mb-1 flex w-full items-center justify-between rounded px-2 py-1 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-300 transition-colors hover:text-gray-100 focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-500"
                  aria-expanded={showAutosaveProjects}
                  onClick={() =>
                    setAutosaveProjectsExpanded(
                      (expanded) => !(expanded ?? defaultShowAutosaveProjects)
                    )
                  }
                >
                  <span className="inline-flex items-center gap-1">
                    {showAutosaveProjects ? (
                      <ChevronDown className="h-3 w-3" aria-hidden />
                    ) : (
                      <ChevronRight className="h-3 w-3" aria-hidden />
                    )}
                    Autosaved projects
                  </span>
                  <span className="text-gray-400">{autosaveProjects.length}</span>
                </button>
                {showAutosaveProjects ? autosaveProjects.map(renderProjectRow) : null}
                {manualProjects.length === 0 ? (
                  <p className="px-2 pb-2 text-xs text-gray-400">
                    No manual cloud projects yet. Autosaves are kept for recovery.
                  </p>
                ) : null}
              </div>
            ) : null}
            {loadingProjects ? <p className="px-2 py-3 text-xs text-gray-400">Loading...</p> : null}
            {projectError ? <p className="px-2 py-3 text-xs text-red-300">{projectError}</p> : null}
            {!loadingProjects && projects.length === 0 && !projectError ? (
              <p className="px-2 py-3 text-xs text-gray-400">No cloud projects yet.</p>
            ) : null}
          </div>
        </section>

        {versionsPanelOpen ? (
          <section className="flex min-h-0 flex-col">
            <div className="border-b border-gray-700 px-3 py-2 text-xs font-medium uppercase tracking-wide text-gray-400">
              {versionsPanelMode === "share" ? "Share" : "Versions"}
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-2">
              {!selectedProject ? (
                <p className="px-2 py-3 text-xs text-gray-400">Select a project.</p>
              ) : null}
              {loadingVersions ? (
                <p className="px-2 py-3 text-xs text-gray-400">Loading...</p>
              ) : null}
              {versionError ? (
                <p className="px-2 py-3 text-xs text-red-300">{versionError}</p>
              ) : null}
              {selectedProject?.isOwner === false ? (
                <div className="mb-3 rounded border border-gray-700 px-2 py-2 text-xs text-gray-300">
                  Shared with {selectedProject.accessRole === "editor" ? "editor" : "viewer"}{" "}
                  access.
                </div>
              ) : null}
              {versionsPanelMode === "share" ? (
                focusedShareVersion ? (
                  <div className="space-y-2">
                    <div className="min-w-0 px-1">
                      <div className="truncate text-sm font-medium text-gray-100">
                        {versionTitle(focusedShareVersion)}
                      </div>
                      <div className="text-xs text-gray-400">
                        #{focusedShareVersion.versionNumber} -{" "}
                        {formatDate(focusedShareVersion.createdAt)}
                      </div>
                    </div>
                    {renderShareLinkPanel(focusedShareVersion)}
                  </div>
                ) : loadingVersions ? null : (
                  <p className="px-2 py-3 text-xs text-gray-400">Preparing share link details...</p>
                )
              ) : (
                <>
                  {manualVersions.length > 0 ? (
                    <div className="mb-3">
                      <div className="mb-1 px-2 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                        Manual saves
                      </div>
                      {manualVersions.map(renderVersionRow)}
                    </div>
                  ) : null}
                  {autosaveVersions.length > 0 ? (
                    <div>
                      <button
                        type="button"
                        className="mb-1 flex w-full items-center justify-between rounded px-2 py-1 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-300 transition-colors hover:text-gray-100 focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-500"
                        aria-expanded={showAutosaveVersions}
                        onClick={() =>
                          setAutosavesExpanded(
                            (expanded) =>
                              !(
                                expanded ??
                                (manualVersions.length === 0 && autosaveVersions.length > 0)
                              )
                          )
                        }
                      >
                        <span className="inline-flex items-center gap-1">
                          {showAutosaveVersions ? (
                            <ChevronDown className="h-3 w-3" aria-hidden />
                          ) : (
                            <ChevronRight className="h-3 w-3" aria-hidden />
                          )}
                          Autosaves
                        </span>
                        <span className="text-gray-400">{autosaveVersions.length}</span>
                      </button>
                      {showAutosaveVersions ? autosaveVersions.map(renderVersionRow) : null}
                    </div>
                  ) : null}
                  {selectedProject &&
                  !loadingVersions &&
                  manualVersions.length === 0 &&
                  autosaveVersions.length > 0 ? (
                    <p className="px-2 pb-2 text-xs text-gray-400">
                      No manual saves yet. Autosaves are kept for recovery.
                    </p>
                  ) : null}
                  {selectedProject && !loadingVersions && versions.length === 0 && !versionError ? (
                    <p className="px-2 py-3 text-xs text-gray-400">No versions yet.</p>
                  ) : null}
                </>
              )}
            </div>
          </section>
        ) : null}
      </div>
    </DraggablePanel>
  );
});

export default CloudProjectsPanel;
